// fetch-клиент к публичным эндпоинтам брони (v1/public/*) и Telegram Mini App
// (v1/public/clubs/:slug/tma/*). Анонимные read-эндпоинты — без авторизации; tma/*
// авторизуются подписанной Telegram initData в заголовке X-Telegram-Init-Data.

import { initDataRaw } from '@/lib/telegram'

const RAW_BASE = import.meta.env.VITE_API_BASE_URL ?? ''
const API_BASE = RAW_BASE.replace(/\/+$/, '')

export class ApiError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, message: string, code: string | null) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
  }
}

function buildUrl(path: string, query?: Record<string, string | undefined>): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost'
  const rawBase = API_BASE || `${origin}/api`
  const base = rawBase.endsWith('/') ? rawBase : rawBase + '/'
  const url = new URL(path.replace(/^\//, ''), base)
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v !== undefined && v !== null) url.searchParams.set(k, v)
    }
  }
  return url.toString()
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  query?: Record<string, string | undefined>
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  // Подписанная Telegram initData — авторизация tma/*-эндпоинтов. Публичные read-
  // эндпоинты её игнорируют, поэтому шлём всегда, когда доступна (не мешает).
  const initData = initDataRaw()
  if (initData) headers['X-Telegram-Init-Data'] = initData
  let payload: BodyInit | undefined
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json'
    payload = JSON.stringify(body)
  }

  let res: Response
  try {
    res = await fetch(buildUrl(path, query), { method, headers, body: payload })
  } catch {
    throw new ApiError(0, 'Нет связи с сервером. Проверьте интернет.', 'network_error')
  }

  const isJson = res.headers.get('content-type')?.includes('application/json')
  const parsed: unknown = isJson ? await res.json().catch(() => null) : await res.text()

  if (!res.ok) {
    const msg =
      (isJson && isRecord(parsed) && typeof parsed.message === 'string' ? parsed.message : null) ??
      `Ошибка запроса (${res.status})`
    const code = isJson && isRecord(parsed) && typeof parsed.code === 'string' ? parsed.code : null
    throw new ApiError(res.status, msg, code)
  }

  return parsed as T
}

// ── Типы публичного API ────────────────────────────────────────
// Код типа игры из справочника клуба (game_types.code): russian/pool/<slug>/…
export type GameKind = string

/** Тип игры для шага выбора: код + отображаемое имя (+ цвет из справочника). */
export interface GameTypeOption {
  code: string
  name: string
  color: string | null
}

export interface PublicClubSummary {
  slug: string
  name: string
  address: string | null
  city: string | null
  description: string | null
  logoUrl: string | null
  timezone: string | null
  gameKinds: GameKind[]
  gameTypes: GameTypeOption[]
  todayHours: { open: string; close: string } | null
}

/** Стол на миникарте зала: живые данные + геометрия из плана (центр глифа). */
export interface FloorMapTable {
  id: string
  name: string
  kind: GameKind | null
  x: number
  y: number
  /** 0 | 90 | 180 | 270 */
  rot: number
  /** индивидуальный масштаб стола, 0.5–2 (1 = базовый размер) */
  scale: number
}

/** Миникарта зала (настраивается в бэкофисе: «Столы» → «План зала»). */
export interface FloorMapData {
  canvas: { w: number; h: number }
  tables: FloorMapTable[]
}

export interface ClubInfo {
  slug: string
  name: string
  timezone: string | null
  slotMinutes: number
  minMinutes: number
  maxMinutes: number
  gameKinds: GameKind[]
  /** Полный список типов игр (код+имя+цвет) для шага выбора. */
  gameTypes: GameTypeOption[]
  /** Разрешён ли клиенту выбор конкретного стола (иначе автоназначение). */
  allowTableChoice: boolean
  currencyDisplay: 'symbol' | 'code' | 'short'
  /** План зала для визуального выбора стола; null — не настроен. */
  floorMap: FloorMapData | null
}

export interface AvailabilitySlot {
  startsAt: number
  endsAt: number
  freeTableIds: string[]
}

export interface AvailabilityResponse {
  date: string
  kind: GameKind | null
  slotMinutes: number
  /** Длительность, на которую посчитаны freeTableIds (= slotMinutes, если не запрошена). */
  durationMinutes: number
  tables: { id: string; name: string; kind: GameKind | null }[]
  slots: AvailabilitySlot[]
}

export interface EquipmentItem {
  id: string
  name: string
  price: number
  deposit: number
}

export interface BookingResult {
  id: string
  tableId: string | null
  gameKind: GameKind | null
  startsAt: number
  endsAt: number
  status: string
  customerName: string
  phone: string
  items: { id: string; inventoryItemId: string; qty: number; name: string | null }[]
}

/** Тело создания брони из Mini App (телефон резолвится сервером по initData). */
export interface TmaCreateBookingPayload {
  name: string
  gameKind?: GameKind
  tableId?: string
  startsAt: number
  endsAt: number
  equipment?: { itemId: string; qty: number }[]
}

/** Профиль пользователя Mini App в клубе. */
export interface TmaMe {
  phone: string | null
  name: string | null
  tgFirstName: string | null
}

/** Элемент списка «Мои брони». */
export interface MyBooking {
  id: string
  tableId: string | null
  tableName: string | null
  tableNumber: number | null
  gameKind: GameKind | null
  startsAt: number
  endsAt: number
  status: string
}

// ── Методы ──────────────────────────────────────────────────────
export const bookingApi = {
  listClubs: () => request<PublicClubSummary[]>('GET', 'v1/public/clubs'),

  clubInfo: (slug: string) => request<ClubInfo>('GET', `v1/public/clubs/${slug}`),

  equipment: (slug: string) => request<EquipmentItem[]>('GET', `v1/public/clubs/${slug}/equipment`),

  // duration — длительность брони в минутах: сервер проверит столы на весь интервал,
  // а не на один шаг сетки (иначе слот показывается свободным, а коммит даёт 409).
  availability: (slug: string, date: string, kind?: GameKind, duration?: number) =>
    request<AvailabilityResponse>('GET', `v1/public/clubs/${slug}/availability`, undefined, {
      date,
      kind,
      duration: duration != null ? String(duration) : undefined
    }),

  // ── Telegram Mini App (авторизация — initData в заголовке) ──
  tmaMe: (slug: string) => request<TmaMe>('GET', `v1/public/clubs/${slug}/tma/me`),

  tmaCreateBooking: (slug: string, payload: TmaCreateBookingPayload) =>
    request<BookingResult>('POST', `v1/public/clubs/${slug}/tma/bookings`, payload),

  tmaMyBookings: (slug: string) =>
    request<MyBooking[]>('GET', `v1/public/clubs/${slug}/tma/bookings`),

  tmaCancelBooking: (slug: string, id: string) =>
    request<{ ok: true }>('POST', `v1/public/clubs/${slug}/tma/bookings/${id}/cancel`)
}
